import { sortedSizes } from '@/domain/catalog/catalog-filters';
import type { IAssistantRow } from '@/interfaces/assistant.interface';
import type { ICartadigitalAdminHostToolDeps } from '../tools/tool-types';
import { isSizeRow, rowEntity } from './row-presenters';
import {
  isAvailabilityValue,
  isNewChoiceValue,
  isOrderValue,
  isPriceValue,
} from './row-values';

/*
 * A product with sizes has the price of its first size on sale (the first
 * active one by `(index, id)`): the catalog's size writer copies that size's
 * price and discounted price into the product whenever that size, or its
 * price, changes (plan P4-1, `applySizes`). A pause, a removal, a new order
 * or a size's new price can therefore change the product's price without
 * a row saying so; the card says it (`assistant.proposal.note.sizes`).
 */

/** A product's size as its price rule reads it. */
export interface ISizeState {
  /** Null: a size the proposal adds. */
  id: number | null;
  active: boolean;
  price: number;
  discountedPrice: number | null;
}

type TSizesContextDeps = Pick<ICartadigitalAdminHostToolDeps, 'menuStore'>;

/** The first size on sale of a list in its order; null when none is. */
function firstActive(sizes: readonly ISizeState[]): ISizeState | null {
  return sizes.find((size) => size.active) ?? null;
}

/** A size's row that the price rule reads: a size's switch, removal or price. */
function sizeRowOf(row: IAssistantRow): boolean {
  return rowEntity(row) === 'size' && row.sizeId !== null;
}

/**
 * Whether the rows of one product (that keeps its sizes) change which of its
 * sizes is the first on sale, or that size's price or discounted price, as
 * the catalog's size writer compares them: the product's price then follows
 * it. `live`: its live sizes in `(index, id)` order. A size order lists the
 * sizes that stay (the others keep their order after them); new sizes go
 * after every size that stays. No size on sale afterwards changes nothing
 * (the product keeps its price).
 */
export function changesFirstSize(
  live: readonly ISizeState[],
  rows: readonly IAssistantRow[]
): boolean {
  let sizes = live.map((size) => ({ ...size }));
  const added: ISizeState[] = [];
  let order: number[] | null = null;
  for (const row of rows) {
    const { to } = row;
    const size = sizeRowOf(row)
      ? sizes.find((candidate) => candidate.id === row.sizeId)
      : undefined;
    switch (row.field) {
      case 'remove':
        if (size) sizes = sizes.filter((candidate) => candidate !== size);
        break;
      case 'availability':
        if (size && isAvailabilityValue(to)) size.active = to.available;
        break;
      case 'price':
        if (size && isPriceValue(to)) {
          size.price = to.price;
          size.discountedPrice = to.discountedPrice;
        }
        break;
      case 'newSize':
        if (isNewChoiceValue(to))
          added.push({
            id: null,
            active: to.active,
            price: to.price,
            discountedPrice: to.discountedPrice,
          });
        break;
      case 'order':
        if (rowEntity(row) === 'item' && isOrderValue(to)) order = to.ids;
        break;
      default:
        break;
    }
  }
  if (order) {
    const listed = order.flatMap((id) => {
      const size = sizes.find((candidate) => candidate.id === id);
      return size ? [size] : [];
    });
    sizes = [...listed, ...sizes.filter((size) => !listed.includes(size))];
  }
  const before = firstActive(live);
  const after = firstActive([...sizes, ...added]);
  if (!after) return false;
  return (
    !before ||
    before.id !== after.id ||
    before.price !== after.price ||
    before.discountedPrice !== after.discountedPrice
  );
}

/**
 * The products (ids, in row order) whose rows change which of their sizes is
 * the first on sale, or its price, read against the menu the panel has
 * loaded: their price follows that size (plan P4-1). A product that
 * switches between one price and sizes is left out (its own row says so),
 * and so is one the menu does not have with sizes.
 */
export function firstSizeChangedIds(
  deps: TSizesContextDeps,
  rows: readonly IAssistantRow[]
): number[] {
  const byProduct = new Map<number, IAssistantRow[]>();
  const switching = new Set<number>();
  for (const row of rows) {
    if (row.itemId === null) continue;
    if (row.field === 'sizes') switching.add(row.itemId);
    else if (isSizeRow(row) || (row.field === 'price' && sizeRowOf(row)))
      byProduct.set(row.itemId, [...(byProduct.get(row.itemId) ?? []), row]);
  }
  const ids: number[] = [];
  for (const [itemId, productRows] of byProduct) {
    if (switching.has(itemId)) continue;
    const item = deps.menuStore.itemMap?.[itemId];
    if (!item) continue;
    const live = sortedSizes(item).flatMap((size): ISizeState[] =>
      typeof size.id === 'number'
        ? [
            {
              id: size.id,
              active: size.active,
              price: size.cost,
              discountedPrice: size.discountedCost ?? null,
            },
          ]
        : []
    );
    if (live.length && changesFirstSize(live, productRows)) ids.push(itemId);
  }
  return ids;
}
